/**
 * MOCK/TEMPORARY — the credit ledger.
 *
 * ============================================================================
 * Credits are client-side only and reset on every page reload. Nothing is
 * persisted and nothing is charged. Spending is enforced in creditsStore, which
 * means it is advisory: a real deployment MUST debit server-side, because a
 * client-side balance is a suggestion.
 * ============================================================================
 */
import { USE_MOCK_API } from '../config';
import { PACKS } from '../data/products';
import { VERIFIED_SIGNUP_CREDITS } from '../lib/credits';
import type { CreditBalance, CreditPack, CreditTransaction } from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';

/**
 * Opening balance for the fixture path: the account's one-time starter credits, unspent.
 */
export const INITIAL_BALANCE: CreditBalance = {
  total: VERIFIED_SIGNUP_CREDITS,
  used: 0,
  reserved: 0,
  unlimited: false,
};

/**
 * What `POST /credits/spend` answers with.
 *
 * ============================================================================
 * `balance` IS THE AUTHORITATIVE FIELD, and it is nullable for one honest reason: the mock
 * has no balance to report. A real endpoint therefore always fills it in. This module may
 * not read a store (see README: "No React, no store access"), so the mock cannot compute
 * what the balance became, and fabricating a number here would be exactly the invented server
 * response the brief forbids.
 *
 * So the store treats a non-null `balance` as final and replaces what it holds, and a null
 * one as "nothing confirmed this" — the deduction stays local and unconfirmed rather than
 * being presented as a settled server fact. When the backend lands, `balance` stops being
 * null and the client stops being the authority, with no change at the call sites.
 * ============================================================================
 */
export interface CreditSpend {
  amount: number;
  reason: string;
  /** The balance after the debit, per contract. Null from the mock, which has none. */
  balance: CreditBalance | null;
}

export const creditsApi = {
  /** MOCK: GET /credits/balance */
  async getBalance(): Promise<CreditBalance> {
    if (!USE_MOCK_API) {
      return http.get<CreditBalance>('/credits');
    }
    assertMock('creditsApi.getBalance');
    return mockDelay({ ...INITIAL_BALANCE });
  },

  /** MOCK: GET /credits/packs — the credit-pack catalogue is static fixture data. */
  async getPacks(): Promise<CreditPack[]> {
    if (!USE_MOCK_API) {
      return http.get<CreditPack[]>('/credits/packs');
    }
    assertMock('creditsApi.getPacks');
    return mockDelay(PACKS);
  },

  /**
   * MOCK: POST /credits/spend
   *
   * A real endpoint is the authority here and rejects with `402` when the balance is
   * insufficient. The mock only echoes the amount and reports `balance: null`, because it
   * has no ledger to debit — see `CreditSpend` for why that null is the honest answer and
   * what the store does with it.
   */
  async spend(amount: number, reason: string): Promise<CreditSpend> {
    if (!USE_MOCK_API) {
      return http.post<CreditSpend>('/credits/spend', { amount, reason });
    }
    assertMock('creditsApi.spend');
    if (amount < 0) {
      throw new ApiError(422, 'Spend amount must be positive');
    }
    return mockDelay({ amount, reason, balance: null });
  },

  /**
   * MOCK: POST /payments/checkout for a credit pack.
   *
   * Takes no money. Returns the pack so the caller knows how many credits to add.
   */
  async purchasePack(packId: string): Promise<CreditPack> {
    if (!USE_MOCK_API) {
      return http.post<CreditPack>(`/credits/packs/${packId}/purchase`);
    }
    assertMock('creditsApi.purchasePack');
    const pack = PACKS.find((p) => p.id === packId);
    if (!pack) {
      throw new ApiError(404, `Unknown credit pack "${packId}"`);
    }
    return mockDelay(pack);
  },

  /**
   * MOCK: GET /credits/transactions
   *
   * Returns nothing. The prototype never displayed a ledger, so there is no
   * fixture history to invent; the store accumulates transactions in memory for
   * the current session and the real endpoint will supply the true history.
   */
  async getTransactions(): Promise<CreditTransaction[]> {
    if (!USE_MOCK_API) {
      return http.get<CreditTransaction[]>('/credits/transactions');
    }
    assertMock('creditsApi.getTransactions');
    return mockDelay([]);
  },
};
